import apiClient from './client';
import type {
  DashboardStats,
  MaintenanceStatus,
  StorageStats,
  CleanupResult,
  NotificationConfig,
  SessionInfo,
} from '@genealogy-app/shared';

// ─── Dashboard ───────────────────────────────────────────────────────────────

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const { data } = await apiClient.get<DashboardStats>('/admin/dashboard');
  return data;
}

// ─── Wartungsmodus ───────────────────────────────────────────────────────────

export async function fetchMaintenanceStatus(): Promise<MaintenanceStatus> {
  const { data } = await apiClient.get<MaintenanceStatus>('/admin/maintenance');
  return data;
}

export async function setMaintenance(
  enabled: boolean,
  message?: string,
): Promise<MaintenanceStatus> {
  const { data } = await apiClient.put<MaintenanceStatus>('/admin/maintenance', { enabled, message });
  return data;
}

export async function updateMaintenanceMessage(message: string): Promise<MaintenanceStatus> {
  const { data } = await apiClient.put<MaintenanceStatus>('/admin/maintenance', { message });
  return data;
}

// ─── Speicherverwaltung ───────────────────────────────────────────────────────

export async function fetchStorageStats(): Promise<StorageStats> {
  const { data } = await apiClient.get<StorageStats>('/admin/storage');
  return data;
}

// ─── Speicher je Konto ───────────────────────────────────────────────────────
//
// Beantwortet die Frage, die /admin/storage nicht beantworten konnte: WER
// belegt den Platz.

export interface KontoSpeicher {
  userId: string;
  email: string;
  tarif: string;
  belegtBytes: number;
  grenzeBytes: number | null;
  prozent: number | null;
  baeume: number;
  dateien: number;
  nachKategorie: { kategorie: string; bytes: number; dateien: number }[];
}

export interface SpeicherAuswertung {
  konten: KontoSpeicher[];
  gesamt: {
    belegtBytes: number;
    dateien: number;
    konten: number;
    knapp: number;
    voll: number;
    nachKategorie: { kategorie: string; bytes: number; dateien: number }[];
  };
  ohneBesitzer: { belegtBytes: number; dateien: number; baeume: number };
}

export async function fetchSpeicherJeKonto(): Promise<SpeicherAuswertung> {
  const { data } = await apiClient.get<SpeicherAuswertung>('/admin/storage/konten');
  return data;
}

export async function runStorageCleanup(): Promise<CleanupResult> {
  const { data } = await apiClient.post<CleanupResult>('/admin/storage/cleanup', {});
  return data;
}

// ─── Benachrichtigungen ───────────────────────────────────────────────────────

export async function fetchNotificationConfig(): Promise<NotificationConfig> {
  const { data } = await apiClient.get<NotificationConfig>('/admin/notifications');
  return data;
}

export async function updateNotificationConfig(
  updates: Partial<NotificationConfig>,
): Promise<NotificationConfig> {
  const { data } = await apiClient.put<NotificationConfig>('/admin/notifications', updates);
  return data;
}

// ─── Sitzungsverwaltung ───────────────────────────────────────────────────────

export async function fetchSessions(): Promise<SessionInfo[]> {
  const { data } = await apiClient.get<SessionInfo[]>('/admin/sessions');
  return data;
}

export async function revokeUserSessions(userId: string): Promise<void> {
  await apiClient.delete(`/admin/sessions/user/${userId}`);
}

export async function revokeAllSessions(): Promise<void> {
  await apiClient.delete('/admin/sessions');
}

// ─── Utility ─────────────────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
